import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error, login_required
  const [organization, setOrganization] = useState(null);
  const [role, setRole] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid invitation link. No token provided.');
      return;
    }

    if (!user || !session) {
      setStatus('login_required');
      return;
    }

    // Accept the invitation
    const acceptInvite = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/members/accept-invite?token=${token}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setOrganization(data.organization);
          setRole(data.role);
          toast.success(data.message);
        } else {
          setStatus('error');
          setErrorMessage(data.detail || data.message || 'Failed to accept invitation');
        }
      } catch (error) {
        setStatus('error');
        setErrorMessage('An error occurred while accepting the invitation');
      }
    };

    acceptInvite();
  }, [token, user, session]);

  const handleLoginRedirect = () => {
    // Store the invite URL to redirect back after login
    sessionStorage.setItem('invite_redirect', window.location.href);
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/app/command-center');
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card-dark border-white/10">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-electric-ice-blue to-blue-600 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">
            {status === 'loading' && 'Processing Invitation...'}
            {status === 'success' && 'Welcome to the Team!'}
            {status === 'error' && 'Invitation Error'}
            {status === 'login_required' && 'Sign In Required'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {status === 'loading' && 'Please wait while we process your invitation'}
            {status === 'success' && `You've successfully joined ${organization?.name}`}
            {status === 'error' && 'We couldn\'t process your invitation'}
            {status === 'login_required' && 'Please sign in to accept this invitation'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {status === 'loading' && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
            </div>
          )}

          {status === 'success' && (
            <>
              <div className="flex items-center justify-center py-4">
                <CheckCircle className="w-16 h-16 text-green-500" />
              </div>
              <div className="text-center space-y-2">
                <p className="text-white font-medium">{organization?.name}</p>
                <p className="text-sm text-gray-400">
                  Role: <span className="text-electric-ice-blue">{role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                </p>
              </div>
              <Button 
                onClick={handleGoToDashboard} 
                className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex items-center justify-center py-4">
                <XCircle className="w-16 h-16 text-red-500" />
              </div>
              <p className="text-center text-red-400">{errorMessage}</p>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5"
              >
                Go to Homepage
              </Button>
            </>
          )}

          {status === 'login_required' && (
            <>
              <p className="text-center text-gray-300">
                You need to sign in or create an account to accept this invitation.
              </p>
              <Button 
                onClick={handleLoginRedirect} 
                className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
                data-testid="login-to-accept-btn"
              >
                Sign In to Accept
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvite;
